import { useQuery } from '@tanstack/react-query';
import { DoorOpen, Hourglass } from '@phosphor-icons/react';
import { useEffect, type ReactNode } from 'react';
import { useT } from '../i18n';
import { api } from '../lib/api';
import { entryView } from '../lib/entry';
import { creditsCount } from '../lib/format';
import { Logo } from './shell/Logo';
import { Button, Card, Toast } from './ui';
import { WaitlistScreen } from './WaitlistScreen';

/**
 * A person without access (spec 9.1): every open asks the API to let them in by the
 * daily quota — a quota raised meanwhile lets them in at once. Quota 0 shows the
 * waitlist form of the closed testing.
 */
export function EntryScreen() {
  const t = useT();
  const { data, isError, isFetching, refetch } = useQuery({
    queryKey: ['access-enter'],
    queryFn: () => api.enter(),
    retry: false,
    staleTime: Infinity,
  });

  const view = isError ? entryView('error') : data ? entryView(data) : null;
  // Let in meanwhile (an invitation in the bot): reload to get the real app.
  useEffect(() => {
    if (view?.kind === 'reload') window.location.reload();
  }, [view?.kind]);
  if (view?.kind === 'closed') return <WaitlistScreen />;

  return (
    <div className="min-h-screen p-6 max-w-lg mx-auto flex flex-col items-center justify-center text-center safe-top safe-bottom">
      <Logo className="mb-6" />
      {view?.kind === 'welcome' && (
        <EntryCard icon={<DoorOpen size={28} />} title={t.entry.welcomeTitle}>
          <p className="text-sm text-hint mb-5">{t.entry.welcomeText(creditsCount(view.credits), view.firstReel)}</p>
          <Button variant="primary" size="lg" fullWidth onClick={() => window.location.reload()}>
            {t.entry.start}
          </Button>
        </EntryCard>
      )}
      {view?.kind === 'full' && (
        <EntryCard icon={<Hourglass size={28} />} title={t.entry.fullTitle}>
          <p className="text-sm text-hint">{t.entry.fullText}</p>
        </EntryCard>
      )}
      {view?.kind === 'error' && (
        <Card variant="surface" padding="none" className="w-full p-5">
          <Toast tone="destructive" className="mb-4">
            {t.common.failed}
          </Toast>
          <Button variant="primary" size="lg" fullWidth loading={isFetching} onClick={() => void refetch()}>
            {t.entry.retry}
          </Button>
        </Card>
      )}
    </div>
  );
}

function EntryCard({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <Card variant="surface" padding="none" className="w-full p-5">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-accent-soft text-accent-soft-on mb-4">
        {icon}
      </div>
      <h1 className="text-xl font-extrabold mb-2">{title}</h1>
      {children}
    </Card>
  );
}
